import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { workspacesService } from '@/services/workspaces';

// Track in-flight requests to prevent duplicate calls (handles React StrictMode double-invocation)
const inFlightRequests: { [key: string]: Promise<any> } = {};

/**
 * Recursively replace Date instances with ISO strings so the slice never
 * holds non-serializable values (Redux warns and DevTools chokes otherwise).
 * Cheap — only walks objects/arrays, leaves primitives alone.
 */
const serializeDates = (obj: any): any => {
  if (!obj || typeof obj !== 'object') return obj;
  if (obj instanceof Date) return obj.toISOString();
  if (Array.isArray(obj)) return obj.map(serializeDates);
  const out: any = {};
  for (const [k, v] of Object.entries(obj)) {
    out[k] = v instanceof Date ? v.toISOString() : (v && typeof v === 'object' ? serializeDates(v) : v);
  }
  return out;
};

export const fetchWorkspaces = createAsyncThunk(
    '/fetchWorkspaces',
    async () => {
        const requestKey = 'workspaces';
        if (inFlightRequests[requestKey]) {
            return inFlightRequests[requestKey];
        }

        const requestPromise = workspacesService.fetchWorkspaces()
            .finally(() => {
                delete inFlightRequests[requestKey];
            });

        inFlightRequests[requestKey] = requestPromise;
        return requestPromise;
    }
)

const workspacesSlice = createSlice({
    name: 'workspaces',
    initialState: {
        workspaces: [] as any[],
        status: 'idle',
        error: null as string | null
    },
    reducers: {
        setWorkspaces: (state, action: PayloadAction<any>) => {
            state.workspaces = serializeDates(action.payload)
            state.status = 'succeeded'
        },
        removeWorkspace: (state, action: PayloadAction<string>) => {
            state.workspaces = (state.workspaces as any[]).filter((w: any) => w.id !== action.payload);
        },
        updateWorkspaceInList: (state, action: PayloadAction<{ id: string; name?: string; description?: string }>) => {
            const { id, ...updates } = action.payload;
            const index = (state.workspaces as any[]).findIndex((w: any) => w.id === id);
            if (index !== -1) {
                (state.workspaces as any[])[index] = { ...(state.workspaces as any[])[index], ...updates };
            }
        },
        // Note: For member changes, we typically refetch the full list since members include user details
        // These events are mainly for UI notification purposes
        workspaceMemberAdded: (state, action: PayloadAction<{ workspace_id: string; user_id: string; email: string; role: string }>) => {
            // Trigger a refetch or update member count - handled by components listening to this
        },
        workspaceMemberRemoved: (state, action: PayloadAction<{ workspace_id: string; user_id: string }>) => {
            // Trigger a refetch or update member count - handled by components listening to this
        }
    },
    extraReducers: (builder) => {
        builder.addCase(fetchWorkspaces.pending, (state, action) => {
            state.status = 'loading'
        })
        builder.addCase(fetchWorkspaces.fulfilled, (state, action) => {
            state.status = 'succeeded'
            state.workspaces = serializeDates(action.payload)
        })
        builder.addCase(fetchWorkspaces.rejected, (state, action) => {
            state.status = 'failed'
            state.error = action.error.message || null
        })
    }
})

export const { setWorkspaces, removeWorkspace, updateWorkspaceInList, workspaceMemberAdded, workspaceMemberRemoved } = workspacesSlice.actions
export default workspacesSlice.reducer